// Marketplace Categories - 3 level hierarchy management
// Path: src/pages/marketplace/MarketplaceCategories.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, Snackbar, Alert, Grid
} from '@mui/material';
import { Add, Edit, Delete, Category as CategoryIcon } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://api.bharatdarshan.app/api';
const UPLOAD_API = 'https://api.bharatdarshan.app/api/vendor/upload-image';
const ASSET_BASE = 'https://api.bharatdarshan.app/';

const MarketplaceCategories = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const handleImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    setUploading(true);
    try {
      const r = await axios.post(UPLOAD_API, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data && r.data.success && r.data.imageUrl) setForm(f => ({ ...f, image: r.data.imageUrl }));
      else showSnack('Upload failed', 'error');
    } catch (err) { showSnack('Upload error: ' + (err.response?.data?.message || err.message), 'error'); }
    setUploading(false);
    e.target.value = '';
  };
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // Data
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);

  // Filters
  const [filterCatId, setFilterCatId] = useState('');
  const [filterSubId, setFilterSubId] = useState('');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(''); // 'category' | 'subcategory' | 'childcategory'
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({});

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const loadCategories = async () => {
    try { const r = await axios.get(`${API}/marketplace/admin/categories`); setCategories(r.data.data || []); }
    catch (e) { showSnack('Failed to load categories', 'error'); }
  };
  const loadSubCategories = async (catId = '') => {
    try {
      const url = catId ? `${API}/marketplace/admin/subcategories?categoryId=${catId}` : `${API}/marketplace/admin/subcategories`;
      const r = await axios.get(url); setSubCategories(r.data.data || []);
    } catch (e) { showSnack('Failed to load subcategories', 'error'); }
  };
  const loadChildCategories = async (subId = '') => {
    try {
      const url = subId ? `${API}/marketplace/admin/childcategories?subCategoryId=${subId}` : `${API}/marketplace/admin/childcategories`;
      const r = await axios.get(url); setChildCategories(r.data.data || []);
    } catch (e) { showSnack('Failed to load child categories', 'error'); }
  };

  useEffect(() => { loadCategories(); loadSubCategories(); loadChildCategories(); }, []);

  const openCreate = (type) => {
    setEditingType(type);
    setEditingItem(null);
    if (type === 'category') setForm({ categoryName: '', image: '' });
    if (type === 'subcategory') setForm({ categoryId: filterCatId || '', subCategoryName: '', image: '', description: '' });
    if (type === 'childcategory') setForm({ categoryId: '', subCategoryId: filterSubId || '', childCategoryName: '', image: '', description: '' });
    setDialogOpen(true);
  };
  const openEdit = (type, item) => {
    setEditingType(type);
    setEditingItem(item);
    if (type === 'category') setForm({ categoryName: item.categoryName, image: item.image || '' });
    if (type === 'subcategory') setForm({ categoryId: item.categoryId?._id || item.categoryId, subCategoryName: item.subCategoryName, image: item.image || '', description: item.description || '' });
    if (type === 'childcategory') setForm({ categoryId: item.categoryId?._id || item.categoryId, subCategoryId: item.subCategoryId?._id || item.subCategoryId, childCategoryName: item.childCategoryName, image: item.image || '', description: item.description || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = editingType === 'category' ? 'categories' : editingType === 'subcategory' ? 'subcategories' : 'childcategories';
      if (editingItem) {
        await axios.put(`${API}/marketplace/admin/${endpoint}/${editingItem._id}`, form);
        showSnack('Updated successfully');
      } else {
        await axios.post(`${API}/marketplace/admin/${endpoint}`, form);
        showSnack('Created successfully');
      }
      setDialogOpen(false);
      loadCategories(); loadSubCategories(filterCatId); loadChildCategories(filterSubId);
    } catch (e) {
      showSnack(e.response?.data?.message || 'Operation failed', 'error');
    }
    setLoading(false);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      const endpoint = type === 'category' ? 'categories' : type === 'subcategory' ? 'subcategories' : 'childcategories';
      await axios.delete(`${API}/marketplace/admin/${endpoint}/${id}`);
      showSnack('Deleted');
      loadCategories(); loadSubCategories(filterCatId); loadChildCategories(filterSubId);
    } catch (e) { showSnack(e.response?.data?.message || 'Delete failed', 'error'); }
  };

  const handleSubCatSelect = (catId) => { setSubCategories([]); loadSubCategories(catId); };
  const handleChildCatSelect = (subId) => { setChildCategories([]); loadChildCategories(subId); };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CategoryIcon sx={{ fontSize: 36, color: '#4CAF50', mr: 2 }} />
        <Typography variant="h4" fontWeight="bold">Marketplace Categories (3-Level)</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}><Card sx={{ bgcolor: '#E8F5E9' }}><CardContent><Typography color="textSecondary">Parent Categories</Typography><Typography variant="h4" fontWeight="bold">{categories.length}</Typography></CardContent></Card></Grid>
        <Grid item xs={12} sm={4}><Card sx={{ bgcolor: '#FFF3E0' }}><CardContent><Typography color="textSecondary">Sub Categories</Typography><Typography variant="h4" fontWeight="bold">{subCategories.length}</Typography></CardContent></Card></Grid>
        <Grid item xs={12} sm={4}><Card sx={{ bgcolor: '#E3F2FD' }}><CardContent><Typography color="textSecondary">Child Categories</Typography><Typography variant="h4" fontWeight="bold">{childCategories.length}</Typography></CardContent></Card></Grid>
      </Grid>

      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Parent Categories" />
            <Tab label="Sub Categories" />
            <Tab label="Child Categories" />
          </Tabs>

          {tab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" color="success" startIcon={<Add />} onClick={() => openCreate('category')}>Add Category</Button>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead><TableRow><TableCell>Image</TableCell><TableCell>Name</TableCell><TableCell>Sub Cats</TableCell><TableCell>Child Cats</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                  <TableBody>
                    {categories.map(c => (
                      <TableRow key={c._id}>
                        <TableCell>{c.image ? <img src={c.image.startsWith('http') ? c.image : `https://api.bharatdarshan.app/${c.image}`} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} /> : '—'}</TableCell>
                        <TableCell>{c.categoryName}</TableCell>
                        <TableCell><Chip label={c.subCount} size="small" color="warning" /></TableCell>
                        <TableCell><Chip label={c.childCount} size="small" color="info" /></TableCell>
                        <TableCell align="right">
                          <IconButton color="primary" onClick={() => openEdit('category', c)}><Edit /></IconButton>
                          <IconButton color="error" onClick={() => handleDelete('category', c._id)}><Delete /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {categories.length === 0 && <TableRow><TableCell colSpan={5} align="center">No categories yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField select size="small" label="Filter by Parent" value={filterCatId} onChange={(e) => { setFilterCatId(e.target.value); handleSubCatSelect(e.target.value); }} sx={{ minWidth: 250 }}>
                  <MenuItem value="">All</MenuItem>
                  {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.categoryName}</MenuItem>)}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" color="warning" startIcon={<Add />} onClick={() => openCreate('subcategory')}>Add Sub Category</Button>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead><TableRow><TableCell>Image</TableCell><TableCell>Name</TableCell><TableCell>Parent Category</TableCell><TableCell>Child Cats</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                  <TableBody>
                    {subCategories.map(s => (
                      <TableRow key={s._id}>
                        <TableCell>{s.image ? <img src={s.image.startsWith('http') ? s.image : `https://api.bharatdarshan.app/${s.image}`} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} /> : '—'}</TableCell>
                        <TableCell>{s.subCategoryName}</TableCell>
                        <TableCell>{s.categoryId?.categoryName || '—'}</TableCell>
                        <TableCell><Chip label={s.childCount} size="small" color="info" /></TableCell>
                        <TableCell align="right">
                          <IconButton color="primary" onClick={() => openEdit('subcategory', s)}><Edit /></IconButton>
                          <IconButton color="error" onClick={() => handleDelete('subcategory', s._id)}><Delete /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {subCategories.length === 0 && <TableRow><TableCell colSpan={5} align="center">No subcategories</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField select size="small" label="Filter by Sub Category" value={filterSubId} onChange={(e) => { setFilterSubId(e.target.value); handleChildCatSelect(e.target.value); }} sx={{ minWidth: 250 }}>
                  <MenuItem value="">All</MenuItem>
                  {subCategories.map(s => <MenuItem key={s._id} value={s._id}>{s.subCategoryName}</MenuItem>)}
                </TextField>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" color="info" startIcon={<Add />} onClick={() => openCreate('childcategory')}>Add Child Category</Button>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead><TableRow><TableCell>Image</TableCell><TableCell>Name</TableCell><TableCell>Parent</TableCell><TableCell>Sub</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                  <TableBody>
                    {childCategories.map(c => (
                      <TableRow key={c._id}>
                        <TableCell>{c.image ? <img src={c.image.startsWith('http') ? c.image : `https://api.bharatdarshan.app/${c.image}`} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} /> : '—'}</TableCell>
                        <TableCell>{c.childCategoryName}</TableCell>
                        <TableCell>{c.categoryId?.categoryName || '—'}</TableCell>
                        <TableCell>{c.subCategoryId?.subCategoryName || '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton color="primary" onClick={() => openEdit('childcategory', c)}><Edit /></IconButton>
                          <IconButton color="error" onClick={() => handleDelete('childcategory', c._id)}><Delete /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {childCategories.length === 0 && <TableRow><TableCell colSpan={5} align="center">No child categories</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit' : 'Add'} {editingType === 'category' ? 'Parent Category' : editingType === 'subcategory' ? 'Sub Category' : 'Child Category'}</DialogTitle>
        <DialogContent>
          {editingType === 'category' && (<>
            <TextField fullWidth label="Category Name" value={form.categoryName || ''} onChange={(e) => setForm({ ...form, categoryName: e.target.value })} margin="normal" />
            <TextField fullWidth label="Image URL or path" value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} margin="normal" helperText="Paste a public URL or upload to assets" />
          </>)}
          {editingType === 'subcategory' && (<>
            <TextField select fullWidth label="Parent Category" value={form.categoryId || ''} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} margin="normal">
              {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.categoryName}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Sub Category Name" value={form.subCategoryName || ''} onChange={(e) => setForm({ ...form, subCategoryName: e.target.value })} margin="normal" />
            <TextField fullWidth label="Image URL or path" value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} margin="normal" />
            <TextField fullWidth label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} margin="normal" multiline rows={2} />
          </>)}
          {editingType === 'childcategory' && (<>
            <TextField select fullWidth label="Parent Category" value={form.categoryId || ''} onChange={(e) => { setForm({ ...form, categoryId: e.target.value, subCategoryId: '' }); loadSubCategories(e.target.value); }} margin="normal">
              {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.categoryName}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Sub Category" value={form.subCategoryId || ''} onChange={(e) => setForm({ ...form, subCategoryId: e.target.value })} margin="normal" disabled={!form.categoryId}>
              {subCategories.filter(s => !form.categoryId || (s.categoryId?._id || s.categoryId) === form.categoryId).map(s => <MenuItem key={s._id} value={s._id}>{s.subCategoryName}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Child Category Name" value={form.childCategoryName || ''} onChange={(e) => setForm({ ...form, childCategoryName: e.target.value })} margin="normal" />
            <TextField fullWidth label="Image URL or path" value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} margin="normal" />
            <TextField fullWidth label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} margin="normal" multiline rows={2} />
          </>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MarketplaceCategories;
